import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

// Public Job Type
export type CareerJob = {
  id: string;
  title: string;
  slug: string;
  location: string;
  employment_type: string | null;
  department: string | null;
  experience_required: string | null;
  min_experience: string | null;
  max_experience: string | null;
  salary_text: string | null;
  short_description: string | null;
  description: string | null;
  responsibilities: string | null;
  qualifications: string | null;
  skills: string | null;
  preferred_skills: string | null;
  working_hours: string | null;
  benefits: string | null;
  application_deadline: string | null;
  additional_information: string | null;
  published_at: string | null;
  created_at: string;
};

const JOB_SELECT_COLS =
  "id, title, slug, location, employment_type, department, experience_required, min_experience, max_experience, salary_text, short_description, description, responsibilities, qualifications, skills, preferred_skills, working_hours, benefits, application_deadline, additional_information, published_at, created_at";

export const listPublishedJobs = createServerFn({ method: "GET" })
  .handler(async (): Promise<CareerJob[]> => {
    const { data, error } = await supabase
      .from("career_jobs")
      .select(JOB_SELECT_COLS)
      .eq("is_published", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[careers] listPublishedJobs failed", error);
      throw new Error("read_failed");
    }

    return (data ?? []) as CareerJob[];
  });

const getBySlugInput = z.object({ slug: z.string() });

export const getJobBySlug = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => getBySlugInput.parse(raw))
  .handler(async ({ data: input }): Promise<CareerJob | null> => {
    // NOTE: use limit(1) rather than maybeSingle() so that legacy duplicate
    // slugs can never turn a valid job page into a 404.
    const { data, error } = await supabase
      .from("career_jobs")
      .select(JOB_SELECT_COLS)
      .eq("slug", input.slug)
      .eq("is_published", true)
      .order("created_at", { ascending: true })
      .limit(1);

    if (error) {
      console.error("[careers] getJobBySlug failed", error);
      throw new Error("read_failed");
    }

    return ((data ?? [])[0] as CareerJob | undefined) ?? null;
  });

const applicationSchema = z.object({
  job_id: z.string().uuid(),
  first_name: z.string().trim().min(1, "First name is required").max(80),
  last_name: z.string().trim().min(1, "Last name is required").max(80),
  phone: z
    .string()
    .trim()
    .regex(/^[0-9+\-\s()]{10,15}$/, "Valid mobile number is required"),
  email: z.string().trim().email("Valid email is required").max(255),
  city: z.string().trim().max(120).optional(),
  total_experience: z.string().trim().max(80).optional(),
  cover_letter: z.string().trim().max(4000).optional(),
  linkedin_url: z.string().trim().max(300).optional(),
  resume_path: z.string().min(1, "Resume is required"),
  resume_original_name: z.string().optional(),
  resume_file_size: z.number().optional(),
  resume_mime_type: z.string().optional(),
});

export type ApplicationInput = z.infer<typeof applicationSchema>;

export const submitApplication = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => applicationSchema.parse(raw))
  .handler(async ({ data }): Promise<{ success: boolean; id?: string; error?: string }> => {
    const { data: result, error } = await supabase.rpc("submit_career_application_v2", {
      p_job_id: data.job_id,
      p_first_name: data.first_name,
      p_last_name: data.last_name,
      p_phone: data.phone,
      p_email: data.email,
      p_city: data.city || undefined,
      p_total_experience: data.total_experience || undefined,
      p_cover_letter: data.cover_letter || undefined,
      p_linkedin_url: data.linkedin_url || undefined,
      p_resume_path: data.resume_path,
      p_resume_original_name: data.resume_original_name || undefined,
      p_resume_file_size: data.resume_file_size ?? undefined,
      p_resume_mime_type: data.resume_mime_type || undefined,
    });

    if (error) {
      console.error("[careers] submitApplication RPC failed", error);
      return { success: false, error: "Submission failed" };
    }

    const typedResult = result as unknown as { success: boolean; id?: string; error?: string };

    if (typedResult.success) {
      const fullName = `${data.first_name} ${data.last_name}`.trim();
      // Best-effort acknowledgement + internal alert.
      const { notifyAdmins, notifyCustomer } = await import("@/lib/notifications/notify.server");
      const rows = [
        { label: "Applicant", value: fullName },
        { label: "Email", value: data.email },
        { label: "Phone", value: data.phone },
        { label: "City", value: data.city || "—" },
        { label: "Total experience", value: data.total_experience || "—" },
        { label: "LinkedIn", value: data.linkedin_url || "—" },
        { label: "Cover letter", value: data.cover_letter || "—" },
      ];
      await notifyCustomer(data.email, {
        subject: "We received your application — Poonam's Healthy Kitchen",
        title: "Application received",
        intro: `Hi ${fullName}, thank you for applying. Our team will review your application and reach out if there's a fit.`,
        rows,
        idempotencyKey: typedResult.id ? `career-ack-${typedResult.id}` : undefined,
      });
      await notifyAdmins({
        subject: `New job application — ${fullName}`,
        title: "New job application",
        intro: "A new application was submitted through the careers page.",
        rows,
        idempotencyKey: typedResult.id ? `career-admin-${typedResult.id}` : undefined,
      });
    }

    return typedResult;
  });
